import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
  getProposal,
  previewProposal,
  proposalPdfUrl,
  updateProposal,
  type ProposalRecord,
} from '../api/proposals'
import { navigate } from '../App'
import { colors } from '../theme'
import StatusPill from '../components/StatusPill'
import ProposalFields from '../components/ProposalFields'
import SendContractModal from '../components/SendContractModal'
import SharePanel from '../components/SharePanel'

// `approved` is the gate for the Send Contract surface — only proposals
// in this state expose the "Send Contract" button. Kept in sync with
// _VALID_STATUSES on the backend.
const STATUSES: ProposalRecord['status'][] = [
  'draft',
  'generated',
  'sent',
  'approved',
  'won',
  'lost',
]

export default function ProposalDetail({ id }: { id: string }) {
  const { authHeaders, apiBase } = useAuth()
  const [rec, setRec] = useState<ProposalRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [showSendContract, setShowSendContract] = useState(false)

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  async function loadPreview() {
    if (!rec) return
    setPreviewing(true)
    try {
      const blob = await previewProposal(apiBase, authHeaders(), rec.payload)
      const url = URL.createObjectURL(blob)
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return url
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed')
    } finally {
      setPreviewing(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    setRec(null)
    setError(null)
    getProposal(apiBase, authHeaders(), id)
      .then((r) => {
        if (!cancelled) setRec(r)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Not found')
      })
    return () => {
      cancelled = true
    }
  }, [id, apiBase, authHeaders])

  async function setStatus(status: ProposalRecord['status']) {
    if (!rec) return
    setBusy(true)
    try {
      const next = await updateProposal(apiBase, authHeaders(), rec.id, { status })
      setRec(next)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed')
    } finally {
      setBusy(false)
    }
  }

  function downloadPdf() {
    if (!rec) return
    const raw = localStorage.getItem('parkify_sales_portal_auth_v1')
    if (!raw) return
    try {
      const parsed = JSON.parse(raw) as { token: string }
      window.open(proposalPdfUrl(apiBase, rec.id, parsed.token), '_blank')
    } catch {
      /* ignore */
    }
  }

  if (error) {
    return (
      <div>
        <BackLink />
        <div
          style={{
            marginTop: 18,
            padding: '14px 18px',
            background: colors.warningBg,
            border: `1.5px solid ${colors.warningBorder}`,
            borderRadius: 10,
            color: colors.black,
            fontSize: 13,
          }}
        >
          <span style={{ color: colors.red, fontWeight: 700, marginRight: 6 }}>
            ⚠
          </span>
          {error}
        </div>
      </div>
    )
  }

  if (!rec) {
    return (
      <div>
        <BackLink />
        <div style={{ marginTop: 18, color: colors.textMuted, fontSize: 13 }}>
          Loading…
        </div>
      </div>
    )
  }

  return (
    <div>
      <BackLink />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginTop: 16,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '2.5px',
              fontWeight: 700,
              color: colors.purple,
              marginBottom: 6,
              textTransform: 'uppercase',
            }}
          >
            Proposal · {rec.id.slice(0, 8).toUpperCase()}
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: '-1px',
              margin: '0 0 8px',
              color: colors.black,
            }}
          >
            {rec.company}
          </h1>
          <div style={{ fontSize: 14, color: colors.textMuted }}>
            {rec.prospect} · {rec.city} · {rec.rep_name ?? rec.rep_email}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusPill status={rec.status} />
          {rec.status === 'approved' ? (
            <button
              onClick={() => setShowSendContract(true)}
              style={{
                background: '#15803D',
                color: colors.white,
                border: 'none',
                borderRadius: 8,
                padding: '10px 18px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              Send Contract
            </button>
          ) : null}
          <button
            onClick={loadPreview}
            disabled={previewing}
            style={{
              background: colors.white,
              color: colors.black,
              border: `1px solid ${colors.divider}`,
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              opacity: previewing ? 0.5 : 1,
              cursor: previewing ? 'wait' : 'pointer',
            }}
          >
            {previewing ? 'Rendering…' : previewUrl ? 'Refresh preview' : 'Preview PDF'}
          </button>
          <button
            onClick={downloadPdf}
            style={{
              background: colors.purple,
              color: colors.white,
              border: 'none',
              borderRadius: 8,
              padding: '10px 18px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Download PDF
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}
      >
        {STATUSES.map((s) => (
          <button
            key={s}
            disabled={busy || rec.status === s}
            onClick={() => setStatus(s)}
            style={{
              background: rec.status === s ? colors.purple : colors.white,
              border: `1px solid ${rec.status === s ? colors.purple : colors.divider}`,
              color: rec.status === s ? colors.white : colors.black,
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            Mark as {s}
          </button>
        ))}
      </div>

      <ProposalFields
        value={rec.payload}
        onChange={() => {
          /* no-op — detail view is read-only for now. Regenerate from "New" */
        }}
        readOnly
      />

      {previewUrl ? (
        <div
          style={{
            marginTop: 18,
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 20px',
              borderBottom: `1px solid ${colors.divider}`,
              background: colors.bgAlt,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                color: colors.purple,
              }}
            >
              PDF preview
            </div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                color: colors.purple,
              }}
            >
              Open in new tab ↗
            </a>
          </div>
          <iframe
            title="Proposal PDF preview"
            src={previewUrl}
            style={{
              width: '100%',
              height: 760,
              border: 'none',
              background: colors.gray,
              display: 'block',
            }}
          />
        </div>
      ) : null}

      {rec.call_notes ? (
        <div
          style={{
            marginTop: 18,
            background: colors.white,
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              color: colors.purple,
              marginBottom: 12,
            }}
          >
            Original call notes
          </div>
          <pre
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.6,
              color: colors.black,
              background: colors.bgAlt,
              border: `1px solid ${colors.divider}`,
              borderRadius: 8,
              padding: '14px 16px',
              fontFamily:
                "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Monaco, monospace",
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {rec.call_notes}
          </pre>
        </div>
      ) : null}

      <SharePanel
        pid={rec.id}
        // The backend refuses to mint a share for a proposal with no rendered
        // PDF; reflect that in the button rather than letting the rep hit a 409.
        hasPdf={Boolean(rec.pdf_gcs_path)}
      />

      <div
        style={{
          marginTop: 18,
          fontSize: 11,
          color: colors.textDim,
          letterSpacing: '0.4px',
        }}
      >
        Created {new Date(rec.created_at).toLocaleString()} · Updated{' '}
        {new Date(rec.updated_at).toLocaleString()}
      </div>

      {showSendContract ? (
        <SendContractModal
          proposalId={rec.id}
          initialOperatorName={rec.company}
          onClose={() => setShowSendContract(false)}
          onSent={(c) => {
            setShowSendContract(false)
            navigate({ name: 'contract', id: c.id })
          }}
        />
      ) : null}
    </div>
  )
}

function BackLink() {
  return (
    <button
      onClick={() => navigate({ name: 'proposals' })}
      style={{
        background: 'transparent',
        border: 'none',
        color: colors.purple,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      ← Back to proposals
    </button>
  )
}
